import { useState, useEffect } from "react";
import { Box, Grid, TextField, Alert, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { grey } from "@mui/material/colors";
import * as Sentry from "@sentry/react";

import { useParams, useNavigate } from "react-router-dom";
import WebIcon from "@mui/icons-material/Web";
import ArticleIcon from "@mui/icons-material/Article";
import ReviewsIcon from "@mui/icons-material/Reviews";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import HubIcon from "@mui/icons-material/Hub";
import GavelIcon from "@mui/icons-material/Gavel";

import IndustryModal from "./IndustryModal";
import LocationModal from "./LocationModal";
import ContentModal from "./ContentModal";
import TrafficModal from "./TrafficModal";
import SentimentModal from "./SentimentModal";
import PoliciesModal from "./PoliciesModal";
import PreviewCard from "./PreviewCard";
import { useTrueBizApi } from "../../../api";
import {
  APICountry,
  APIMccCode,
} from "../../../types/APICompanyMatchProfileConstants";
import { OFACS_COUNTRY_CODES } from "../constants";
import APICompanyMatchProfile from "../../../types/APICompanyMatchProfile";

function ActionButton({
  readOnly,
  matchProfileId,
  submitted,
  loading,
  handleSaveProfile,
}: {
  readOnly: boolean;
  matchProfileId: string | undefined;
  submitted: boolean;
  loading: boolean;
  handleSaveProfile: any;
}) {
  if (matchProfileId === "default") {
    return null;
  }

  if (readOnly) {
    return (
      <Button
        sx={{ position: "absolute", top: -55, right: 0 }}
        href={`${window.location.origin}/risk_profile/edit/${matchProfileId}`}
        variant="contained"
        color="primary"
        size="large"
      >
        Edit
      </Button>
    );
  }

  return (
    <LoadingButton
      variant="contained"
      size="large"
      onClick={handleSaveProfile}
      disabled={submitted}
      loading={loading}
      sx={{ position: "absolute", top: -55, right: 0 }}
    >
      SAVE
    </LoadingButton>
  );
}

const getLocalStorageKey = (matchProfileId: string | undefined) => {
  const suffix = matchProfileId || "default"; // can be undefined
  // need a version in case we change defaults or logic and need to drop cache
  return `matchProfile-${suffix}-v1`;
};

export default function MatchProfileForm({ readOnly }: { readOnly: boolean }) {
  let { matchProfileId } = useParams();
  const navigate = useNavigate();

  const api = useTrueBizApi();
  const [matchProfiles, setMatchProfiles] = useState<
    null | APICompanyMatchProfile[]
  >(null);
  const [parentMatchProfile, setParentMatchProfile] =
    useState<null | APICompanyMatchProfile>(null);

  const [openModal, setOpenModal] = useState<null | string>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [submitted, setSubmitted] = useState(false);
  const [mccData, setMccData] = useState<null | APIMccCode[]>(null);
  const [countries, setCountries] = useState<null | APICountry[]>(null);
  const [contentCategories, setContentCategories] = useState<null | string[]>(
    null
  );
  const [defaultMatchProfile, setDefaultMatchProfile] = useState<null | any>(
    null
  );

  const [matchProfile, setMatchProfile] =
    useState<null | APICompanyMatchProfile>(null);

  useEffect(() => {
    // call the prerequisite APIs, in parallel, and set the data to state
    const loadMatchProfiles = async () => {
      if ([undefined, "default"].includes(matchProfileId)) {
        // dont get match profiles if this isn't editing another profile
        return [];
      }
      return await api.getCompanyMatchProfiles();
    };

    Promise.all([
      api.getCompanyMatchProfileConstants(),
      loadMatchProfiles(),
    ]).then(([constants, matchProfiles]) => {
      setMccData(constants.mcc_codes);
      setCountries(constants.countries);
      setContentCategories(constants.default_content_categories);
      setMatchProfiles(matchProfiles);
    });
  }, [api, matchProfileId]);

  useEffect(() => {
    // wait for all the API calls to be done
    // then set matchProfile, defaultMatchProfile, and parentMatchProfile

    // if state is not set for all data from APIs, exit
    if (
      countries === null ||
      contentCategories === null ||
      mccData === null ||
      matchProfiles === null
    ) {
      return;
    }

    // if the matchProfile has already been set, exit
    if (matchProfile !== null) {
      return;
    }

    const initDefaultMatchProfile = () => {
      // get and set the defaultMatchProfile, which is the same as the TrueBiz Default
      const defaultProfile: APICompanyMatchProfile = {
        id: null,
        status: null,
        created_at: null,
        updated_at: null,
        name: matchProfileId === "default" ? "TrueBiz Default" : "",
        enable_adverse_media: true,
        enable_negative_business_reviews: true,
        enable_paid_search_rate: true,
        enable_time_on_site: true,
        enable_country_traffic: true,
        enable_referring_site: true,
        enable_bounce_rate: true,
        enable_policies: true,
        red_country_codes:
          countries
            ?.filter((country: any) =>
              OFACS_COUNTRY_CODES.includes(country.code)
            )
            .map((country: any) => country.code) || [],
        yellow_country_codes: [],
        red_mcc_codes: [],
        yellow_mcc_codes: [],
        red_content_categories: contentCategories ? [...contentCategories] : [],
        yellow_content_categories: [],
      };
      setDefaultMatchProfile(defaultProfile);
      return defaultProfile;
    };

    const initParentMatchProfile = () => {
      if ([undefined, "default"].includes(matchProfileId)) {
        return null;
      }

      const parentProfile = matchProfiles?.find(
        (mp) => mp.id === matchProfileId
      );

      if (!parentProfile) {
        throw new Error(`unable to find matchProfile ${matchProfileId}`);
      }
      setParentMatchProfile(parentProfile);

      return parentProfile;
    };

    const getMatchProfileFromLocalStorage = () => {
      const storedMatchProfileString = localStorage.getItem(
        getLocalStorageKey(matchProfileId)
      );
      if (storedMatchProfileString === null) {
        return null;
      }

      try {
        return JSON.parse(storedMatchProfileString || "");
      } catch (e) {
        // if there's a problem loading from localstorage, just go back to the default
        Sentry.captureException(e);
        return null;
      }
    };

    // set defaultMatchProfile state, but return the object so we don't have to wait
    const defaultProfile = initDefaultMatchProfile();

    // set parenttMatchProfile state, but return the object so we don't have to wait
    const parentProfile = initParentMatchProfile();

    // if we're just viewing a profile, set it here and
    if (parentProfile && readOnly) {
      setMatchProfile(structuredClone(parentProfile));
      return;
    }

    // look for a match profile in localstorage
    const fromLocal = getMatchProfileFromLocalStorage();
    if (fromLocal !== null) {
      // if there was one in localstorage, use it
      setMatchProfile(structuredClone(fromLocal));
    } else if (parentProfile !== null) {
      // if not, but there was a parent profile, use that but clear the name
      let parentCopy = structuredClone(parentProfile);
      parentCopy.name = "";
      setMatchProfile(parentCopy);
    } else {
      // if no parent and nothing from local, this is fully new and start from default
      setMatchProfile(structuredClone(defaultProfile));
    }
  }, [
    countries,
    contentCategories,
    mccData,
    matchProfiles,
    matchProfileId,
    matchProfile,
    readOnly,
  ]);

  useEffect(() => {
    // as changes are made, persist it to localstorage

    // don't clear the cache before match profile is loaded
    // but allow clearing cache after form is successfully submitted
    if (matchProfile === null && submitted === false) {
      return;
    }

    localStorage.setItem(
      getLocalStorageKey(matchProfileId),
      JSON.stringify(matchProfile)
    );
  }, [matchProfile, matchProfileId, submitted]);

  const updateMatchProfile = (update: any) => {
    // check if there's any nulls in the lists that need to be all strings
    const hasNullKeys = [
      "yellow_mcc_codes",
      "red_mcc_codes",
      "red_country_codes",
      "yellow_country_codes",
    ].filter(
      (key) =>
        key in update &&
        update[key].some((value: string) => typeof value !== "string")
    );

    // if there are nulls, throw an error for each key
    hasNullKeys.forEach((key) => {
      try {
        throw new Error(
          `tried to add a non-string value to ${key} - ${update[key]}`
        );
      } catch (e) {
        Sentry.captureException(e);
      }
    });

    // if there are null keys, don't update the match profile
    if (hasNullKeys.length > 0) {
      return;
    }

    setMatchProfile({ ...matchProfile, ...update });
  };

  const handleSaveProfile = async () => {
    if (!matchProfile) {
      return;
    }

    if (hasAnyChanges() === false) {
      setErrorMessage(
        "Nothing has been changed from default settings. Please make a change or use the default risk profile."
      );
      return null;
    }

    if (!matchProfile.name.length) {
      setErrorMessage("Name is required.");
      return null;
    }

    if (parentMatchProfile && parentMatchProfile.name === matchProfile.name) {
      setErrorMessage("A new name is required when changing a risk profile.");
      return null;
    }

    try {
      setSubmitted(true);
      setErrorMessage(null);
      setLoading(true);
      await api.createMatchProfile(matchProfile);

      navigate("/risk_profiles");
    } catch (error) {
      Sentry.captureException(error);
      setSubmitted(false);
      setErrorMessage(
        "Internal error. We have been alerted to the problem and are working on a fix. We apologize for the inconvenience."
      );
    } finally {
      setLoading(false);
    }
  };

  const isIndustryEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    if (
      matchProfile.yellow_mcc_codes.sort().toString() !==
      baseline.yellow_mcc_codes.sort().toString()
    ) {
      return true;
    }
    if (
      matchProfile.red_mcc_codes.sort().toString() !==
      baseline.red_mcc_codes.sort().toString()
    ) {
      return true;
    }

    return false;
  };

  const isContentEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    if (
      matchProfile.yellow_content_categories.sort().toString() !==
      baseline.yellow_content_categories.sort().toString()
    ) {
      return true;
    }
    if (
      matchProfile.red_content_categories.sort().toString() !==
      baseline.red_content_categories.sort().toString()
    ) {
      return true;
    }

    return false;
  };

  const isLocationEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    if (
      matchProfile.yellow_country_codes.sort().toString() !==
      baseline.yellow_country_codes.sort().toString()
    ) {
      return true;
    }
    if (
      matchProfile.red_country_codes.sort().toString() !==
      baseline.red_country_codes.sort().toString()
    ) {
      return true;
    }

    return false;
  };

  const isTrafficEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    return (
      matchProfile.enable_paid_search_rate !==
        baseline.enable_paid_search_rate ||
      matchProfile.enable_time_on_site !== baseline.enable_time_on_site ||
      matchProfile.enable_country_traffic !== baseline.enable_country_traffic ||
      matchProfile.enable_referring_site !== baseline.enable_referring_site ||
      matchProfile.enable_bounce_rate !== baseline.enable_bounce_rate
    );
  };
  const isSentimentEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    return (
      matchProfile.enable_adverse_media !== baseline.enable_adverse_media ||
      matchProfile.enable_negative_business_reviews !==
        baseline.enable_negative_business_reviews
    );
  };

  const isPolicyEdited = () => {
    if (!matchProfile) return false;

    const baseline = parentMatchProfile || defaultMatchProfile;
    if (!baseline) return false;

    return matchProfile.enable_policies !== baseline.enable_policies;
  };

  const hasAnyChanges = () => {
    if (isContentEdited()) return true;
    if (isIndustryEdited()) return true;
    if (isLocationEdited()) return true;
    if (isPolicyEdited()) return true;
    if (isSentimentEdited()) return true;
    if (isTrafficEdited()) return true;

    return false;
  };

  const handleClearChanges = () => {
    if (parentMatchProfile) {
      const parentCopy = structuredClone(parentMatchProfile);
      parentCopy.name = "";
      setMatchProfile(parentCopy);
    } else {
      setMatchProfile(structuredClone(defaultMatchProfile));
    }
  };

  if (
    !matchProfile ||
    !defaultMatchProfile ||
    !mccData ||
    !countries ||
    !contentCategories
  ) {
    return null;
  }

  return (
    <>
      <Box sx={{ height: 70 }}>
        <span>
          <Typography variant="h4" component="span" fontWeight={700}>
            {readOnly ? "View Risk Profile" : "New Risk Profile"}
          </Typography>
        </span>

        {parentMatchProfile && !readOnly && (
          <Typography
            variant="h6"
            component="span"
            sx={{ ml: 10, height: "100%" }}
          >
            Starting from: <strong>{parentMatchProfile.name}</strong>
          </Typography>
        )}
      </Box>

      {parentMatchProfile && !readOnly && (
        <Typography
          variant="subtitle2"
          sx={{ width: 280, mt: -1, color: grey[800] }}
        >
          Risk profiles cannot be edited once saved. This page creates a new
          risk profile starting from the settings of another profile.
        </Typography>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ pb: 4, mt: 2 }}
      >
        <Box
          sx={{ width: "100%", maxWidth: 1150, position: "relative", pt: 5 }}
        >
          {errorMessage && (
            <Alert sx={{ mb: 3 }} severity="error">
              {errorMessage}
            </Alert>
          )}

          {!readOnly && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleClearChanges}
              disabled={!matchProfile.name && !hasAnyChanges()}
              sx={{ position: "absolute", top: -55, right: 100 }}
            >
              Clear Changes
            </Button>
          )}

          <ActionButton
            readOnly={readOnly}
            matchProfileId={matchProfileId}
            submitted={submitted}
            loading={loading}
            handleSaveProfile={handleSaveProfile}
          />

          <Box
            sx={{
              height: 30,
              position: "absolute",
              top: -54,
              left: 0,
              right: 0,
              marginInline: "auto",
              width: "fit-content",
              minWidth: 400,
              maxWidth: 800,
            }}
          >
            {readOnly ? (
              <Typography variant="h4">
                <span style={{ color: grey[600] }}>Name: </span>
                {matchProfile.name}
              </Typography>
            ) : (
              <TextField
                disabled={loading}
                label="Risk Profile Name"
                value={matchProfile.name}
                onChange={(e) => updateMatchProfile({ name: e.target.value })}
                type="text"
                sx={{ width: "100%", height: 30 }}
              />
            )}
          </Box>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("industry")}
                title="Industry Classification"
                mainText="TrueBiz classifies every business using the MCC system. This section allows you to select which MCC categories should be flagged."
                icon={<HubIcon></HubIcon>}
                isEdited={isIndustryEdited()}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("content")}
                title="Content"
                mainText="TrueBiz has a set of flags that are evaluated based on content on a business's website. This section allows configuring which of these you should be alerted to."
                icon={<ArticleIcon></ArticleIcon>}
                isEdited={isContentEdited()}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("location")}
                title="Location"
                mainText="This section allows all countries in the world to be assigned as high risk or prohibited. These settings will alert you based on a business's primary location."
                icon={<EditLocationAltIcon></EditLocationAltIcon>}
                isEdited={isLocationEdited()}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("traffic")}
                title="Traffic"
                mainText="TrueBiz tracks a variety of website traffic data. This section allows you to activate several risks that could flag businesses based on this traffic data."
                icon={<WebIcon></WebIcon>}
                isEdited={isTrafficEdited()}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("sentiment")}
                title="Sentiment"
                mainText="TrueBiz can flag businesses for having poor review sentiment. Configure whether or not this is activated here."
                icon={<ReviewsIcon></ReviewsIcon>}
                isEdited={isSentimentEdited()}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PreviewCard
                clickHandler={() => setOpenModal("policies")}
                title="Policies"
                mainText="TrueBiz can evaluate a business's website policies, such as returns and terms of service. Choose here whether or not this is activated."
                icon={<GavelIcon></GavelIcon>}
                isEdited={isPolicyEdited()}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>
        </Box>
        <LocationModal
          open={openModal === "location"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          countries={countries}
          readOnly={readOnly}
        />
        <IndustryModal
          open={openModal === "industry"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          mccData={mccData}
          readOnly={readOnly}
        />
        <ContentModal
          open={openModal === "content"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          contentCategories={contentCategories}
          readOnly={readOnly}
        />
        <TrafficModal
          open={openModal === "traffic"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          readOnly={readOnly}
        />
        <SentimentModal
          open={openModal === "sentiment"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          readOnly={readOnly}
        />
        <PoliciesModal
          open={openModal === "policies"}
          setOpenModal={setOpenModal}
          matchProfile={matchProfile}
          updateMatchProfile={updateMatchProfile}
          readOnly={readOnly}
        />
      </Box>
    </>
  );
}
